(function (root) {
  "use strict";

  /*
   * Live Audiocolour metronome audio engine.
   *
   * Public API:
   *   AudiocolourMetronomeAudio.createEngine({ sound, volume, onEvent })
   *   engine.start(settings) -> Promise<{ ok, ... }>
   *   engine.stop() / engine.setVolume(0..100) / engine.setSound(name) /
   *   engine.getState() / engine.destroy()
   *
   * Event callbacks with type "scheduled" are emitted after source.start(time)
   * has been queued, but before that sound plays. `scheduledTime` is in the
   * AudioContext time coordinate and can drive a synchronized visual display.
   * To change settings, stop and then start again with the complete new settings;
   * settings are intentionally not patched during a running sequence.
   * setSound changes the buffer used by subsequently scheduled events; sources
   * already queued keep their existing AudioBuffer and finish unchanged.
   *
   * Phase-safe envelope bounds are built from every supported meter,
   * subdivision and swing setting. Continuous-time interval overlap is used,
   * with each envelope bin padded by one source sample to cover arbitrary
   * fractional start phases and interpolation. Switching sounds can briefly
   * dip the master volume while queued old-profile clicks finish; this keeps
   * the combined output within the same headroom target.
   */
  var POLL_INTERVAL_MS = 25;
  var LOOKAHEAD_SECONDS = 0.125;
  var MIN_SCHEDULE_LEAD_SECONDS = 0.005;
  var START_LEAD_SECONDS = 0.05;
  var STOP_FADE_SECONDS = 0.005;
  var VOLUME_RAMP_SECONDS = 0.015;
  var RESUME_TIMEOUT_MS = 1500;
  var MAX_MASTER_GAIN = 0.45;
  var MAX_CLICK_PEAK = 0.5;
  var WORST_CASE_OVERLAP = 9;
  var SAFE_OUTPUT_PEAK = 0.8912509381337456 / Math.pow(10, 3 / 20);
  var PROFILE_MAX_GAINS = Object.freeze({
    classic: MAX_MASTER_GAIN,
    hihat: MAX_MASTER_GAIN,
    woodblock: MAX_MASTER_GAIN,
    // The beep's measured per-rate envelope applies the additional cap below.
    beep: Math.min(MAX_MASTER_GAIN, 0.194)
  });
  var envelopeSafetyCache = Object.create(null);

  function failure(code, message, details) {
    return { ok: false, error: Object.assign({ code: code, message: message }, details || {}) };
  }
  function success(value) { return Object.assign({ ok: true }, value || {}); }
  function finite(value) { return typeof value === "number" && Number.isFinite(value); }
  function isInput(value) { return !!value && typeof value === "object" && !Array.isArray(value); }
  function errorText(error) { return String(error && error.message || error || "unknown error"); }
  function contextConstructor() { return root.AudioContext || root.webkitAudioContext || null; }
  function currentTime(context) {
    try { return context && finite(context.currentTime) ? context.currentTime : null; }
    catch (_) { return null; }
  }
  function latencyValue(context, name) {
    try {
      var value = context && context[name];
      return finite(value) && value >= 0 ? value : null;
    } catch (_) { return null; }
  }

  function dependencies() {
    var core = root.AudiocolourMetronomeCore;
    var sounds = root.AudiocolourMetronomeSounds;
    if (!core || !sounds) {
      return failure("missing_dependency", "Load the metronome rhythm core and sound module before creating an audio engine.");
    }
    return success({ core: core, sounds: sounds });
  }

  function validateVolume(volume) {
    if (!finite(volume) || volume < 0 || volume > 100) {
      return failure("invalid_volume", "Volume must be a finite number from 0 to 100.");
    }
    return success({ volume: volume });
  }

  function gainForVolume(volume, maximumGain) {
    if (volume === 0) return 0;
    return maximumGain * Math.pow(10, (-40 + 40 * (volume - 1) / 99) / 20);
  }

  function validateSound(sound, sounds) {
    if (sound === undefined) sound = "classic";
    var result = sounds.validateSound(sound);
    return result.ok ? success({ sound: result.sound }) : result;
  }

  function overlapForDuration(core, durationSeconds) {
    var maximum = 0;
    var denominators = core.denominators || [];
    var subdivisions = core.subdivisions || [];
    var bpm = core.bpmRange && core.bpmRange.max;
    if (!finite(bpm) || bpm <= 0 || !denominators.length || !subdivisions.length) return null;
    for (var d = 0; d < denominators.length; d += 1) {
      for (var s = 0; s < subdivisions.length; s += 1) {
        var subdivision = subdivisions[s];
        var swings = [50];
        if (subdivision === 2 || subdivision === 4) {
          for (var percent = core.swingRange.min + 1; percent <= core.swingRange.max; percent += 1) swings.push(percent);
          swings.push(core.swingRange.triplet);
        }
        for (var w = 0; w < swings.length; w += 1) {
          var settings = { bpm: bpm, numerator: core.numeratorRange.max, denominator: denominators[d], subdivision: subdivision, swingPercent: swings[w] };
          var iteratorResult = core.createEventIterator(settings, { bars: 1 });
          if (!iteratorResult.ok) continue;
          var validated = core.validateSettings(settings);
          if (!validated.ok) continue;
          var barSeconds = validated.settings.barTicks * 60 / (bpm * core.ticksPerQuarter);
          var times = [];
          while (true) {
            var next = iteratorResult.iterator.next();
            if (next.done) break;
            times.push(next.value.positionTicks * 60 / (bpm * core.ticksPerQuarter));
          }
          for (var i = 0; i < times.length; i += 1) {
            var count = 0;
            for (var j = 0; j < times.length; j += 1) {
              var delta = j >= i ? times[j] - times[i] : times[j] + barSeconds - times[i];
              if (durationSeconds > delta) count += Math.ceil((durationSeconds - delta) / barSeconds);
            }
            if (count > maximum) maximum = count;
          }
        }
      }
    }
    return maximum || null;
  }

  var minimumSpacingTicksCache = null;

  function minimumSpacingTicks(core) {
    if (minimumSpacingTicksCache !== null) return minimumSpacingTicksCache;
    var minimum = Infinity;
    var bpm = core.bpmRange.max;
    for (var numerator = core.numeratorRange.min; numerator <= core.numeratorRange.max; numerator += 1) {
      for (var d = 0; d < core.denominators.length; d += 1) {
        for (var s = 0; s < core.subdivisions.length; s += 1) {
          var swings = [core.swingRange.straight];
          if (core.subdivisions[s] === 2 || core.subdivisions[s] === 4) {
            for (var swing = core.swingRange.min + 1; swing <= core.swingRange.max; swing += 1) swings.push(swing);
            swings.push(core.swingRange.triplet);
          }
          for (var w = 0; w < swings.length; w += 1) {
            var settings = { bpm: bpm, numerator: numerator, denominator: core.denominators[d], subdivision: core.subdivisions[s], swingPercent: swings[w] };
            var validated = core.validateSettings(settings);
            var result = core.createEventIterator(settings, { bars: 1 });
            if (!validated.ok || !result.ok) return null;
            var previous = null;
            var first = null;
            while (true) {
              var next = result.iterator.next();
              if (next.done) break;
              var tick = next.value.positionTicks;
              if (first === null) first = tick;
              if (previous !== null) minimum = Math.min(minimum, tick - previous);
              previous = tick;
            }
            if (first !== null && previous !== null) minimum = Math.min(minimum, validated.settings.barTicks - previous + first);
          }
        }
      }
    }
    minimumSpacingTicksCache = finite(minimum) && minimum > 0 ? minimum : null;
    return minimumSpacingTicksCache;
  }

  function envelopeBoundForSound(core, soundName, clicks, sampleRate) {
    var cacheKey = soundName + ":" + sampleRate;
    if (envelopeSafetyCache[cacheKey]) return envelopeSafetyCache[cacheKey];
    var levelNames = ["accent", "normal", "soft", "subdivision"];
    var maxLength = 0;
    for (var l = 0; l < levelNames.length; l += 1) {
      var click = clicks[levelNames[l]];
      if (!click || !click.samples || !click.samples.length) return null;
      maxLength = Math.max(maxLength, click.samples.length);
    }
    var maxDuration = maxLength / sampleRate;
    var minTicks = minimumSpacingTicks(core);
    var maxBpm = core.bpmRange.max;
    if (!minTicks || !finite(maxBpm) || maxBpm <= 0) return null;
    var minimumGapFrames = minTicks * 60 * sampleRate / (maxBpm * core.ticksPerQuarter);
    var requiredFrameGap = Math.max(1, Math.ceil(minimumGapFrames) - 1);
    var envelope = new Float64Array(maxLength);
    for (var frame = 0; frame < maxLength; frame += 1) {
      var peak = 0;
      // Keep bins exactly [frame / rate, (frame + 1) / rate). The sample
      // padding only widens the value bound, never the time interval.
      for (var levelIndex = 0; levelIndex < levelNames.length; levelIndex += 1) {
        var samples = clicks[levelNames[levelIndex]].samples;
        for (var sampleIndex = Math.max(0, frame - 1); sampleIndex <= Math.min(frame + 1, samples.length - 1); sampleIndex += 1) {
          peak = Math.max(peak, Math.abs(samples[sampleIndex]));
        }
      }
      envelope[frame] = peak;
    }

    // At any instant, active click ages are separated by at least this gap:
    // the smallest supported tick interval at the maximum 300 BPM. Slower
    // tempos only increase that gap. Max-weight interval scheduling over the
    // disjoint one-frame age bins therefore bounds every tempo continuously,
    // every meter/swing pattern, every phase, and every choice of sound level.
    var best = new Float64Array(maxLength + 1);
    for (var ageBin = 0; ageBin < maxLength; ageBin += 1) {
      var compatiblePrefix = Math.max(0, ageBin - requiredFrameGap + 1);
      best[ageBin + 1] = Math.max(best[ageBin], envelope[ageBin] + best[compatiblePrefix]);
    }
    var maximum = best[maxLength];
    if (maximum > 0) {
      var result = { bound: maximum * (1 + 1e-9), maxClickSeconds: maxDuration, minimumGapSeconds: minTicks * 60 / (maxBpm * core.ticksPerQuarter) };
      envelopeSafetyCache[cacheKey] = result;
      return result;
    }
    return null;
  }

  function createEngine(options) {
    var deps = dependencies();
    if (!deps.ok) return deps;
    options = options === undefined ? {} : options;
    if (!isInput(options)) return failure("invalid_options", "Engine options must be an object.");

    var soundCheck = validateSound(options.sound, deps.sounds);
    if (!soundCheck.ok) return soundCheck;
    var volumeCheck = validateVolume(options.volume === undefined ? 80 : options.volume);
    if (!volumeCheck.ok) return volumeCheck;
    if (options.onEvent !== undefined && typeof options.onEvent !== "function") {
      return failure("invalid_callback", "onEvent must be a function when provided.");
    }
    if (typeof root.setTimeout !== "function" || typeof root.clearTimeout !== "function") {
      return failure("unsupported", "Browser timers are unavailable.");
    }

    var context = null;
    var master = null;
    var soundCache = Object.create(null);
    var selectedSoundEntry = null;
    var soundSampleRate = null;
    var gainAutomation = { from: 0, to: 0, start: 0, end: 0 };
    var timer = null;
    var status = "idle";
    var volume = volumeCheck.volume;
    var runToken = 0;
    var activeRun = null;
    var pendingStartupCancel = null;
    var sources = [];
    var lastError = null;
    var destroyed = false;
    var onEvent = options.onEvent || null;
    var sound = soundCheck.sound;
    var selectedMasterGainLimit = PROFILE_MAX_GAINS[sound];
    var activeMasterGainLimit = selectedMasterGainLimit;
    var contextStateHandler = null;
    var pageHideHandler = null;
    var visibilityHandler = null;

    function notify(event) {
      if (!onEvent) return;
      try { onEvent(Object.freeze(event)); } catch (_) { /* Consumer callbacks cannot break scheduling. */ }
    }

    function stateSnapshot() {
      return Object.freeze({
        status: status,
        sound: sound,
        volume: volume,
        maxMasterGain: activeMasterGainLimit,
        selectedMaxMasterGain: selectedMasterGainLimit,
        sampleRate: context ? context.sampleRate : null,
        soundSampleRate: soundSampleRate,
        maxClickDurationSeconds: selectedSoundEntry ? selectedSoundEntry.maxClickSeconds : null,
        maxOverlapEnvelope: selectedSoundEntry ? selectedSoundEntry.envelopeBound : null,
        baseLatency: latencyValue(context, "baseLatency"),
        outputLatency: latencyValue(context, "outputLatency"),
        startTime: activeRun ? activeRun.startTime : null,
        scheduledCount: activeRun ? activeRun.scheduledCount : 0,
        skippedLateCount: activeRun ? activeRun.skippedLateCount : 0,
        activeSourceCount: sources.length,
        error: lastError
      });
    }

    function removeSource(source) {
      var index = sources.indexOf(source);
      if (index !== -1) sources.splice(index, 1);
      refreshActiveGainLimit(true);
    }

    function disconnectSource(source) {
      try { source.disconnect(); } catch (_) { /* Already disconnected or context closed. */ }
      removeSource(source);
    }

    function refreshActiveGainLimit(rampIfPlaying) {
      var totalBound = selectedSoundEntry ? selectedSoundEntry.envelopeBound : 0;
      var seen = Object.create(null);
      seen[sound] = true;
      if (status === "playing") {
        sources.forEach(function (source) {
          if (finite(source._audiocolourStopTime) && source._audiocolourStopTime <= lastStartTime) return;
          var sourceSound = source._audiocolourSound;
          if (!sourceSound || seen[sourceSound]) return;
          seen[sourceSound] = true;
          totalBound += source._audiocolourEnvelopeBound || 0;
        });
      }
      var nextLimit = Math.min(selectedMasterGainLimit, SAFE_OUTPUT_PEAK / totalBound);
      var changed = Math.abs(nextLimit - activeMasterGainLimit) > 1e-12;
      activeMasterGainLimit = nextLimit;
      if (changed && rampIfPlaying && status === "playing" && context && master && master.gain) {
        var now = currentTime(context);
        if (now !== null) {
          var rampStart = now < stopFadeUntil ? Math.max(stopFadeUntil, lastStartTime - VOLUME_RAMP_SECONDS) : now;
          var duration = rampStart < lastStartTime ? Math.min(VOLUME_RAMP_SECONDS, lastStartTime - rampStart) : VOLUME_RAMP_SECONDS;
          try { rampGainTo(gainForVolume(volume, activeMasterGainLimit), rampStart, duration); }
          catch (error) {
            lastError = { code: "audio_node_failed", message: "Could not update gain for active sound buffers.", cause: errorText(error) };
            stopInternal("audio_error");
          }
        }
      }
      return activeMasterGainLimit;
    }

    function clearTimer() {
      if (timer !== null) {
        try { root.clearTimeout(timer); } catch (_) {}
        timer = null;
      }
    }

    function setGainAt(time, value) {
      if (!master || !master.gain) return;
      var param = master.gain;
      if (typeof param.cancelScheduledValues === "function") param.cancelScheduledValues(time);
      if (typeof param.setValueAtTime === "function") param.setValueAtTime(value, time);
      else param.value = value;
      gainAutomation = { from: value, to: value, start: time, end: time };
    }

    function audioRateForContext() {
      var range = deps.sounds.sampleRateRange;
      if (!range || !finite(range.min) || !finite(range.max) || range.min <= 0 || range.max < range.min) return null;
      return Math.round(Math.min(range.max, Math.max(range.min, context.sampleRate)));
    }

    function prepareAudioBuffers(soundName) {
      var rate = audioRateForContext();
      if (!rate) return failure("unsupported_sample_rate", "The sound module does not provide a usable sample-rate range.");
      var key = String(rate) + ":" + soundName;
      if (soundCache[key]) return success({ entry: soundCache[key] });
      var generated = deps.sounds.generateSoundSet(soundName, rate);
      if (!generated.ok) return failure("sound_generation_failed", generated.error.message, { cause: generated.error.code });
      var nextBuffers = {};
      var maxClickPeak = 0;
      var maxClickSeconds = 0;
      try {
        ["accent", "normal", "soft", "subdivision"].forEach(function (level) {
          var click = generated.clicks[level];
          if (!click || click.sampleRate !== rate || click.samples.length < 1 || click.length !== click.samples.length) {
            throw new Error("The sound module returned an invalid " + level + " click buffer.");
          }
          for (var i = 0; i < click.samples.length; i += 1) {
            if (!finite(click.samples[i]) || Math.abs(click.samples[i]) > MAX_CLICK_PEAK) {
              throw new Error("The sound module returned unsafe samples for " + level + ".");
            }
            maxClickPeak = Math.max(maxClickPeak, Math.abs(click.samples[i]));
          }
          maxClickSeconds = Math.max(maxClickSeconds, click.samples.length / rate);
          var buffer = context.createBuffer(1, click.samples.length, rate);
          var channel = buffer.getChannelData(0);
          channel.set(click.samples);
          nextBuffers[level] = buffer;
        });
      } catch (error) {
        return failure("audio_buffer_failed", "Could not prepare reusable click buffers.", { cause: errorText(error) });
      }
      var overlap = overlapForDuration(deps.core, maxClickSeconds);
      var envelope = envelopeBoundForSound(deps.core, soundName, generated.clicks, rate);
      var verifiedBound = envelope && finite(envelope.bound) ? envelope.bound : null;
      var maximumGain = verifiedBound ? Math.min(PROFILE_MAX_GAINS[soundName], SAFE_OUTPUT_PEAK / verifiedBound) : 0;
      if (overlap === null || overlap > WORST_CASE_OVERLAP || !verifiedBound || maximumGain <= 0 || verifiedBound * maximumGain > SAFE_OUTPUT_PEAK) {
        return failure("unsafe_sound_profile", "The generated click duration or peak exceeds the engine's verified overlap-safety budget.");
      }
      var entry = Object.freeze({ soundSet: generated, audioBuffers: Object.freeze(nextBuffers), sampleRate: rate, maxClickSeconds: maxClickSeconds, envelopeBound: verifiedBound, maxMasterGain: maximumGain });
      soundCache[key] = entry;
      return success({ entry: entry });
    }

    function prepareAllAudioBuffers(preferredSound) {
      var names = deps.sounds.soundNames || [];
      var ordered = [preferredSound];
      var preferredEntry = null;
      names.forEach(function (name) { if (name !== preferredSound) ordered.push(name); });
      for (var i = 0; i < ordered.length; i += 1) {
        var result = prepareAudioBuffers(ordered[i]);
        if (!result.ok) return result;
        if (ordered[i] === preferredSound) preferredEntry = result.entry;
      }
      return success({ entry: preferredEntry });
    }

    function gainAt(time) {
      if (!gainAutomation || time <= gainAutomation.start) return gainAutomation ? gainAutomation.from : 0;
      if (time >= gainAutomation.end || gainAutomation.end <= gainAutomation.start) return gainAutomation.to;
      return gainAutomation.from + (gainAutomation.to - gainAutomation.from) * ((time - gainAutomation.start) / (gainAutomation.end - gainAutomation.start));
    }

    function rampGainTo(value, start, duration) {
      if (!master || !master.gain) return;
      var param = master.gain;
      var from = gainAt(start);
      var end = start + duration;
      if (typeof param.cancelAndHoldAtTime === "function") {
        param.cancelAndHoldAtTime(start);
        // cancelAndHoldAtTime preserves the held value, but explicitly anchor
        // the mathematically computed value used by this engine's automation.
        if (typeof param.setValueAtTime === "function") param.setValueAtTime(from, start);
        else param.value = from;
      } else {
        if (typeof param.cancelScheduledValues === "function") param.cancelScheduledValues(start);
        if (typeof param.setValueAtTime === "function") param.setValueAtTime(from, start);
        else param.value = from;
      }
      if (duration <= 0 || typeof param.linearRampToValueAtTime !== "function") {
        if (typeof param.setValueAtTime === "function") param.setValueAtTime(value, end);
        else param.value = value;
      } else {
        param.linearRampToValueAtTime(value, end);
      }
      gainAutomation = { from: from, to: value, start: start, end: end };
    }

    function ensureContext() {
      if (context && context.state !== "closed") return success({ context: context });
      if (context && context.state === "closed") {
        removeContextListener();
        sources.slice().forEach(disconnectSource);
        if (master) {
          try { master.disconnect(); } catch (_) {}
        }
        context = null;
        master = null;
        soundCache = Object.create(null);
        selectedSoundEntry = null;
        soundSampleRate = null;
        stopFadeUntil = 0;
      }
      var Constructor = contextConstructor();
      if (typeof Constructor !== "function") return failure("unsupported", "Web Audio is unavailable in this browser.");
      try {
        context = new Constructor();
      } catch (error) {
        context = null;
        return failure("context_failed", "AudioContext creation failed.", { cause: errorText(error) });
      }
      if (!context || !finite(context.sampleRate) || context.sampleRate <= 0 ||
          typeof context.createGain !== "function" || typeof context.createBuffer !== "function" ||
          typeof context.createBufferSource !== "function") {
        if (context && typeof context.close === "function") {
          try { Promise.resolve(context.close()).catch(function () {}); } catch (_) {}
        }
        context = null;
        return failure("context_failed", "AudioContext does not provide the required Web Audio interfaces.");
      }
      try {
        master = context.createGain();
        master.connect(context.destination);
        setGainAt(currentTime(context) || 0, 0);
      } catch (error) {
        if (master) { try { master.disconnect(); } catch (_) {} }
        if (context && typeof context.close === "function") {
          try { Promise.resolve(context.close()).catch(function () {}); } catch (_) {}
        }
        master = null;
        context = null;
        return failure("context_failed", "Could not create the master volume node.", { cause: errorText(error) });
      }
      installContextListener();
      return success({ context: context });
    }

    function installContextListener() {
      if (!context || typeof context.addEventListener !== "function") return;
      contextStateHandler = function () {
        if (destroyed || !context || context.state === "running") return;
        if (status === "playing") stopInternal("context_interrupted");
      };
      try { context.addEventListener("statechange", contextStateHandler); } catch (_) { contextStateHandler = null; }
    }

    function removeContextListener() {
      if (!context || !contextStateHandler) return;
      try { context.removeEventListener("statechange", contextStateHandler); } catch (_) {}
      contextStateHandler = null;
    }

    function waitForRunning(ctx, resumePromise) {
      return new Promise(function (resolve) {
        var done = false;
        var timeout = null;
        var stateHandler = null;
        function finish(result) {
          if (done) return;
          done = true;
          if (timeout !== null) root.clearTimeout(timeout);
          if (stateHandler && typeof ctx.removeEventListener === "function") {
            try { ctx.removeEventListener("statechange", stateHandler); } catch (_) {}
          }
          pendingStartupCancel = null;
          resolve(result);
        }
        pendingStartupCancel = function () { finish(failure("cancelled", "Playback start was cancelled.")); };
        if (ctx.state === "running") { finish(success({})); return; }
        stateHandler = function () {
          if (ctx.state === "running") finish(success({}));
          else if (ctx.state === "closed") finish(failure("context_interrupted", "AudioContext closed while starting."));
        };
        if (typeof ctx.addEventListener === "function") {
          try { ctx.addEventListener("statechange", stateHandler); } catch (_) {}
        }
        timeout = root.setTimeout(function () {
          finish(failure("playback_blocked", "AudioContext did not enter the running state in time.", { timedOut: true, contextState: ctx.state }));
        }, RESUME_TIMEOUT_MS);
        Promise.resolve(resumePromise).then(function () {
          if (ctx.state === "running") finish(success({}));
          else if (ctx.state === "closed") finish(failure("context_interrupted", "AudioContext closed while starting."));
          else finish(failure("playback_blocked", "AudioContext resume completed without starting audio.", { contextState: ctx.state }));
        }, function (error) {
          finish(failure("playback_blocked", "AudioContext resume failed.", { cause: errorText(error), contextState: ctx.state }));
        });
      });
    }

    function pullNext(run) {
      run.pending = run.iterator.next();
    }

    function scheduledEvent(run, event, audioTime, observedTime) {
      return {
        type: "scheduled",
        barIndex: event.barIndex,
        beatIndex: event.beatIndex,
        groupIndex: event.groupIndex,
        subdivisionIndex: event.subdivisionIndex,
        accentLevel: event.accentLevel,
        isCountIn: event.isCountIn,
        positionTicks: event.positionTicks,
        scheduledTime: audioTime,
        observedTime: observedTime,
        sampleRate: context.sampleRate,
        soundSampleRate: soundSampleRate,
        sound: sound
      };
    }

    function scheduleWindow() {
      var run = activeRun;
      if (!run || status !== "playing" || !context || context.state !== "running") return success({});
      var now = currentTime(context);
      if (now === null) return failure("context_error", "AudioContext returned an invalid currentTime.");
      var horizon = now + LOOKAHEAD_SECONDS;
      var minimumTime = now + MIN_SCHEDULE_LEAD_SECONDS;
      while (run.pending && !run.pending.done && status === "playing") {
        var event = run.pending.value;
        var offset = deps.core.ticksToSeconds(event.positionTicks, run.settings.bpm);
        if (!offset.ok || !finite(offset.seconds)) return failure("timing_error", "Could not convert a rhythm event to audio time.");
        var audioTime = run.startTime + offset.seconds;
        if (audioTime < minimumTime) {
          run.skippedLateCount += 1;
          notify({
            type: "skipped",
            reason: "late",
            barIndex: event.barIndex,
            beatIndex: event.beatIndex,
            subdivisionIndex: event.subdivisionIndex,
            accentLevel: event.accentLevel,
            isCountIn: event.isCountIn,
            positionTicks: event.positionTicks,
            scheduledTime: audioTime,
            observedTime: now
          });
          pullNext(run);
          continue;
        }
        if (audioTime > horizon) break;

        var buffer = selectedSoundEntry && selectedSoundEntry.audioBuffers[event.accentLevel];
        if (!buffer) return failure("invalid_event", "Rhythm core produced an unsupported or muted event level.");
        var source;
        try {
          source = context.createBufferSource();
          source.buffer = buffer;
          source._audiocolourSound = sound;
          source._audiocolourEnvelopeBound = selectedSoundEntry.envelopeBound;
          source.connect(master);
          source.onended = (function (scheduledSource) {
            return function () { disconnectSource(scheduledSource); };
          }(source));
          sources.push(source);
          source.start(audioTime);
        } catch (error) {
          if (source) disconnectSource(source);
          return failure("audio_schedule_failed", "Could not schedule a click sound.", { cause: errorText(error) });
        }
        run.scheduledCount += 1;
        notify(scheduledEvent(run, event, audioTime, now));
        pullNext(run);
      }
      return success({});
    }

    function armScheduler() {
      if (status !== "playing" || timer !== null) return;
      try {
        timer = root.setTimeout(function () {
          timer = null;
          if (status !== "playing") return;
          var result;
          try { result = scheduleWindow(); }
          catch (error) { result = failure("audio_schedule_failed", "The audio scheduler failed.", { cause: errorText(error) }); }
          if (!result.ok) {
            lastError = result.error;
            stopInternal("audio_error");
            notify({ type: "error", error: result.error });
            return;
          }
          armScheduler();
        }, POLL_INTERVAL_MS);
      } catch (error) {
        return failure("timer_failed", "Could not start the audio scheduler timer.", { cause: errorText(error) });
      }
      return success({});
    }

    function stopInternal(reason) {
      if (status === "destroyed" || status === "stopped" || status === "idle") return success({ alreadyStopped: true });
      runToken += 1;
      if (pendingStartupCancel) pendingStartupCancel();
      clearTimer();
      var wasStarting = status === "starting";
      status = "stopped";
      activeRun = null;
      var now = context ? currentTime(context) : null;
      if (now !== null && master && master.gain) {
        try {
          var stopAt = now + STOP_FADE_SECONDS;
          var existingFadeActive = now < stopFadeUntil;
          if (existingFadeActive) {
            // Preserve the first stop's 5 ms fade, but remove any later
            // restarted-run automation scheduled beyond its end.
            setGainAt(stopFadeUntil + 0.000001, 0);
          } else {
            stopFadeUntil = stopAt;
            var beforeFirstSound = context && now < (lastStartTime || 0);
            if (beforeFirstSound) setGainAt(now, 0);
            else rampGainTo(0, now, STOP_FADE_SECONDS);
          }
          sources.slice().forEach(function (source) {
            if (existingFadeActive && finite(source._audiocolourStopTime) && source._audiocolourStopTime <= stopFadeUntil) return;
            source._audiocolourStopTime = stopAt;
            try { source.stop(stopAt); } catch (_) { disconnectSource(source); }
          });
        } catch (_) {
          sources.slice().forEach(disconnectSource);
        }
      } else {
        sources.slice().forEach(function (source) {
          try { source.stop(); } catch (_) {}
          disconnectSource(source);
        });
      }
      notify({ type: "stopped", reason: reason || (wasStarting ? "cancelled" : "user"), audioTime: now });
      return success({ stopped: true });
    }

    var lastStartTime = 0;
    var stopFadeUntil = 0;

    function start(settings) {
      if (destroyed) return Promise.resolve(failure("engine_destroyed", "Audio engine has been destroyed."));
      if (status === "playing" || status === "starting") return Promise.resolve(failure("already_running", "Stop the current run before starting again."));
      if (!isInput(settings)) return Promise.resolve(failure("invalid_settings", "Metronome settings must be an object."));
      var validated = deps.core.validateSettings(settings);
      if (!validated.ok) return Promise.resolve(failure(validated.error.code, validated.error.message, validated.error));
      if (root.document && root.document.visibilityState === "hidden") {
        return Promise.resolve(failure("page_hidden", "Playback can start only while the page is visible."));
      }
      var iteratorResult = deps.core.createEventIterator(settings);
      if (!iteratorResult.ok) return Promise.resolve(failure(iteratorResult.error.code, iteratorResult.error.message, iteratorResult.error));
      var contextResult = ensureContext();
      if (!contextResult.ok) {
        status = "stopped";
        lastError = contextResult.error;
        return Promise.resolve(contextResult);
      }
      var ctx = contextResult.context;
      var resumePromise;
      try {
        if (ctx.state === "running") resumePromise = Promise.resolve();
        else if (typeof ctx.resume === "function") resumePromise = ctx.resume();
        else return Promise.resolve(failure("playback_blocked", "AudioContext cannot resume."));
      } catch (error) {
        lastError = { code: "playback_blocked", message: "AudioContext resume failed.", cause: errorText(error) };
        status = "stopped";
        return Promise.resolve({ ok: false, error: lastError });
      }
      status = "starting";
      lastError = null;
      var token = ++runToken;
      return waitForRunning(ctx, resumePromise).then(function (running) {
        if (destroyed || token !== runToken) return failure("cancelled", "Playback start was cancelled.");
        if (!running.ok) {
          status = "stopped";
          lastError = running.error;
          return running;
        }
        var rate;
        try { rate = ctx.sampleRate; } catch (error) {
          status = "stopped";
          return failure("context_error", "Could not read AudioContext.sampleRate.", { cause: errorText(error) });
        }
        if (!finite(rate) || rate <= 0) {
          status = "stopped";
          return failure("context_error", "AudioContext returned an invalid sample rate.");
        }
        // Generate and validate every profile before any playback is queued.
        // Subsequent setSound() calls then only select already prepared buffers.
        var buffers = prepareAllAudioBuffers(sound);
        if (!buffers.ok) {
          status = "stopped";
          lastError = buffers.error;
          return buffers;
        }
        var now = currentTime(ctx);
        if (now === null || ctx.state !== "running") {
          status = "stopped";
          return failure("context_interrupted", "AudioContext stopped before playback could begin.");
        }
        var startTime = Math.max(now + START_LEAD_SECONDS, stopFadeUntil + START_LEAD_SECONDS);
        selectedSoundEntry = buffers.entry;
        soundSampleRate = buffers.entry.sampleRate;
        selectedMasterGainLimit = buffers.entry.maxMasterGain;
        activeMasterGainLimit = selectedMasterGainLimit;
        try {
          rampGainTo(gainForVolume(volume, activeMasterGainLimit), startTime - VOLUME_RAMP_SECONDS, VOLUME_RAMP_SECONDS);
        } catch (error) {
          status = "stopped";
          return failure("audio_node_failed", "Could not set the master volume.", { cause: errorText(error) });
        }
        lastStartTime = startTime;
        activeRun = {
          settings: validated.settings,
          iterator: iteratorResult.iterator,
          pending: null,
          startTime: startTime,
          scheduledCount: 0,
          skippedLateCount: 0
        };
        pullNext(activeRun);
        status = "playing";
        var firstWindow;
        try { firstWindow = scheduleWindow(); }
        catch (error) { firstWindow = failure("audio_schedule_failed", "Could not schedule initial clicks.", { cause: errorText(error) }); }
        if (!firstWindow.ok) {
          lastError = firstWindow.error;
          stopInternal("audio_error");
          return firstWindow;
        }
        var timerResult = armScheduler();
        if (timerResult && !timerResult.ok) {
          lastError = timerResult.error;
          stopInternal("timer_failed");
          return timerResult;
        }
        var result = success({ sound: sound, sampleRate: rate, soundSampleRate: soundSampleRate, startTime: startTime, settings: validated.settings });
        notify({ type: "started", sound: sound, sampleRate: rate, soundSampleRate: soundSampleRate, startTime: startTime });
        return result;
      });
    }

    function setVolume(nextVolume) {
      if (destroyed) return failure("engine_destroyed", "Audio engine has been destroyed.");
      var checked = validateVolume(nextVolume);
      if (!checked.ok) return checked;
      volume = checked.volume;
      if (status === "playing" && context && master && master.gain) {
        var now = currentTime(context);
        if (now !== null) {
          var rampStart = now < stopFadeUntil ? Math.max(stopFadeUntil, lastStartTime - VOLUME_RAMP_SECONDS) : now;
          var rampDuration = rampStart < lastStartTime ? Math.min(VOLUME_RAMP_SECONDS, lastStartTime - rampStart) : VOLUME_RAMP_SECONDS;
          try { rampGainTo(gainForVolume(volume, activeMasterGainLimit), rampStart, rampDuration); }
          catch (error) { return failure("audio_node_failed", "Could not update master volume.", { cause: errorText(error) }); }
        }
      } else if (status === "stopped" && context && master && master.gain && sources.length === 0 && currentTime(context) >= stopFadeUntil) {
        var stoppedAt = currentTime(context);
        if (stoppedAt !== null) {
          try { rampGainTo(gainForVolume(volume, activeMasterGainLimit), stoppedAt, VOLUME_RAMP_SECONDS); }
          catch (error) { return failure("audio_node_failed", "Could not update master volume.", { cause: errorText(error) }); }
        }
      }
      notify({ type: "volume", volume: volume, gain: gainForVolume(volume, activeMasterGainLimit) });
      return success({ volume: volume, gain: gainForVolume(volume, activeMasterGainLimit) });
    }

    function setSound(nextSound) {
      if (destroyed) return failure("engine_destroyed", "Audio engine has been destroyed.");
      var checked = validateSound(nextSound, deps.sounds);
      if (!checked.ok) return checked;
      var nextEntry = null;
      if (context && context.state !== "closed") {
        var prepared = prepareAudioBuffers(checked.sound);
        if (!prepared.ok) return prepared;
        nextEntry = prepared.entry;
      }
      sound = checked.sound;
      if (nextEntry) {
        selectedSoundEntry = nextEntry;
        soundSampleRate = nextEntry.sampleRate;
        selectedMasterGainLimit = nextEntry.maxMasterGain;
      } else {
        selectedSoundEntry = null;
        soundSampleRate = null;
        selectedMasterGainLimit = PROFILE_MAX_GAINS[sound];
      }
      refreshActiveGainLimit(true);
      notify({ type: "sound", sound: sound, soundSampleRate: soundSampleRate, maxMasterGain: activeMasterGainLimit });
      return success({ sound: sound, soundSampleRate: soundSampleRate, maxMasterGain: activeMasterGainLimit });
    }

    function stop() {
      if (destroyed) return success({ alreadyStopped: true });
      return stopInternal("user");
    }

    function destroy() {
      if (destroyed) return success({ alreadyDestroyed: true });
      stopInternal("destroyed");
      destroyed = true;
      clearTimer();
      if (root.document && visibilityHandler && typeof root.document.removeEventListener === "function") {
        try { root.document.removeEventListener("visibilitychange", visibilityHandler); } catch (_) {}
      }
      if (pageHideHandler && typeof root.removeEventListener === "function") {
        try { root.removeEventListener("pagehide", pageHideHandler); } catch (_) {}
      }
      removeContextListener();
      if (master) {
        try { master.disconnect(); } catch (_) {}
        master = null;
      }
      sources.slice().forEach(disconnectSource);
      soundCache = Object.create(null);
      selectedSoundEntry = null;
      soundSampleRate = null;
      if (context && typeof context.close === "function" && context.state !== "closed") {
        try { Promise.resolve(context.close()).catch(function () {}); } catch (_) {}
      }
      context = null;
      activeRun = null;
      status = "destroyed";
      notify({ type: "destroyed" });
      onEvent = null;
      return success({ destroyed: true });
    }

    pageHideHandler = function () { if (status === "playing" || status === "starting") stopInternal("page_hidden"); };
    visibilityHandler = function () {
      if (root.document && root.document.visibilityState === "hidden" && (status === "playing" || status === "starting")) {
        stopInternal("page_hidden");
      }
    };
    try { if (typeof root.addEventListener === "function") root.addEventListener("pagehide", pageHideHandler); } catch (_) {}
    try {
      if (root.document && typeof root.document.addEventListener === "function") {
        root.document.addEventListener("visibilitychange", visibilityHandler);
      }
    } catch (_) {}

    var engine = {
      start: start,
      stop: stop,
      setVolume: setVolume,
      setSound: setSound,
      getState: stateSnapshot,
      destroy: destroy
    };
    return success({ engine: Object.freeze(engine) });
  }

  root.AudiocolourMetronomeAudio = Object.freeze({
    version: "1.0.0",
    limits: Object.freeze({
      pollIntervalMs: POLL_INTERVAL_MS,
      lookaheadSeconds: LOOKAHEAD_SECONDS,
      minimumScheduleLeadSeconds: MIN_SCHEDULE_LEAD_SECONDS,
      startLeadSeconds: START_LEAD_SECONDS,
      resumeTimeoutMs: RESUME_TIMEOUT_MS,
      volumeRampSeconds: VOLUME_RAMP_SECONDS,
      maxMasterGain: MAX_MASTER_GAIN,
      maxMasterGainBySound: PROFILE_MAX_GAINS,
      maximumClickPeak: MAX_CLICK_PEAK,
      worstCaseOverlap: WORST_CASE_OVERLAP,
      outputPeakCeiling: 0.8912509381337456,
      safetyTargetDbBelowCeiling: 3
    }),
    isSupported: function () { return typeof contextConstructor() === "function"; },
    createEngine: createEngine
  });
}(globalThis));
